import { Alert, App as AntApp, Button, Grid, Select, Space, Table, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { ApiError, errorLines } from "../app/problems.ts";
import { formatDate } from "../app/classes.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { announcementKeys, statusColor, type Announcement, type AnnouncementStatus } from "../app/announcements.ts";
import { AnnouncementComposerDrawer } from "../components/AnnouncementComposerDrawer.tsx";

const PAGE_SIZE = 20;
const statuses = Object.keys(statusColor) as AnnouncementStatus[];

/**
 * School announcements (bd-3qg.19): the history with delivery counts for everyone with announcements.read (the
 * directors too), and the composer and cancel action for announcements.send.
 */
export function AnnouncementsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { message } = AntApp.useApp();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const canSend = hasPermission("announcements.send");
  const [composing, setComposing] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<AnnouncementStatus | undefined>(undefined);

  const query = useQuery({
    queryKey: announcementKeys.list({ page, status }),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await api.GET("/announcements", {
        params: { query: { Page: page, PageSize: PAGE_SIZE, ...(status === undefined ? {} : { Status: status }) } },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await api.POST("/announcements/{id}/cancel", { params: { path: { id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      void message.success(t("announcements.history.cancelled"));
    },
    onError: (err) => void message.error(errorLines(err, t("app.error")).join(" ")),
  });

  const columns = [
    {
      key: "announcement",
      title: t("announcements.history.columns.announcement"),
      render: (_: unknown, a: Announcement) => (
        <Space orientation="vertical" size={2} style={{ width: "100%" }}>
          <Typography.Text strong>{a.title}</Typography.Text>
          <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }} ellipsis={{ rows: 2, expandable: true }}>
            {a.text}
          </Typography.Paragraph>
          <Typography.Text type="secondary">
            {t("announcements.history.by", { name: a.createdByName ?? "—", time: formatSchoolDateTime(a.createdAt) })}
          </Typography.Text>
        </Space>
      ),
    },
    { key: "audience", title: t("announcements.history.columns.audience"), width: 180, render: (_: unknown, a: Announcement) => <AudienceText announcement={a} /> },
    { key: "status", title: t("announcements.history.columns.status"), width: 160, render: (_: unknown, a: Announcement) => <StatusCell announcement={a} /> },
    { key: "delivery", title: t("announcements.history.columns.delivery"), width: 240, render: (_: unknown, a: Announcement) => <DeliveryCell announcement={a} /> },
    ...(canSend
      ? [
          {
            key: "actions",
            title: t("common.actions"),
            width: 120,
            render: (_: unknown, a: Announcement) =>
              a.status === "Scheduled" ? (
                <Button size="small" danger loading={cancel.isPending && cancel.variables === a.id} onClick={() => cancel.mutate(a.id)}>
                  {t("announcements.history.cancel")}
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("announcements.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("announcements.subtitle")}</Typography.Text>
        </div>
        {canSend && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposing(true)}>
            {t("announcements.new")}
          </Button>
        )}
      </div>
      <Select<AnnouncementStatus | undefined>
        allowClear
        value={status}
        placeholder={t("announcements.history.allStatuses")}
        aria-label={t("announcements.history.statusFilter")}
        style={{ width: screens.md ? 220 : "100%" }}
        options={statuses.map((s) => ({ value: s, label: t(`announcements.statuses.${s}`) }))}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
      />
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<Announcement>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("announcements.history.empty") }}
        scroll={{ x: 960 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total: number) => t("announcements.history.total", { count: total }),
          onChange: setPage,
        }}
      />
      {canSend && <AnnouncementComposerDrawer open={composing} onClose={() => setComposing(false)} />}
    </Space>
  );
}

function AudienceText({ announcement: a }: { announcement: Announcement }) {
  const { t } = useTranslation();
  const detail =
    a.audience === "Grades"
      ? a.grades.map((g) => t("announcements.gradeLabel", { grade: Number(g) })).join(", ")
      : a.audience === "Classes"
        ? a.classNames.join(", ")
        : null;
  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text>{t(`announcements.audiencesShort.${a.audience}`)}</Typography.Text>
      {detail && <Typography.Text type="secondary">{detail}</Typography.Text>}
    </Space>
  );
}

function StatusCell({ announcement: a }: { announcement: Announcement }) {
  const { t } = useTranslation();
  const time = a.status === "Sent" ? a.sentAt : a.status === "Cancelled" ? a.cancelledAt : a.scheduledAt;
  return (
    <Space orientation="vertical" size={2}>
      <Tag color={statusColor[a.status]}>{t(`announcements.statuses.${a.status}`)}</Tag>
      {time && <Typography.Text type="secondary">{formatSchoolDateTime(time)}</Typography.Text>}
    </Space>
  );
}

function DeliveryCell({ announcement: a }: { announcement: Announcement }) {
  const { t } = useTranslation();
  if (a.status !== "Sent") return <Typography.Text type="secondary">—</Typography.Text>;
  const d = a.delivery;
  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text>{t("announcements.history.queued", { count: Number(a.queuedCount), date: a.recipientDate ? formatDate(a.recipientDate) : "" })}</Typography.Text>
      <Typography.Text type="secondary">
        {t("announcements.history.counts", {
          sent: Number(d.sent),
          pending: Number(d.pending),
          failed: Number(d.failed),
          unreachable: Number(d.unreachable),
          skipped: Number(d.skipped),
        })}
      </Typography.Text>
      <Typography.Text type={Number(a.notLinkedCount) > 0 ? "warning" : "secondary"}>{t("announcements.history.notLinked", { count: Number(a.notLinkedCount) })}</Typography.Text>
    </Space>
  );
}
